/**
 * The subtitle editor's decisions, as pure functions: what a typed time means for a cue, where a
 * split cuts, which keys undo, what a row shows, and the windowed row source the editable table
 * reads. No DOM, so the tests drive all of it directly (web/test/editor-page.test.mjs).
 *
 * The DOM half is lib/cue-editor.ts. The document itself never comes to this side: rows arrive a
 * page at a time from `preview`, and every change goes to the worker as an `editor` message
 * (src/editor/session.ts) naming a cue by id.
 */

import type { DocumentId, WriteFormat } from '../../../../../contracts/cue-model.ts';
import type { CuePreviewRow, PreviewPayload } from '../../../../../src/core/protocol.ts';
import type { EditRefusal } from '../../../../../src/editor/ops.ts';
import { type CueTimeError, formatCueTime, parseCueTime } from '../../../../../src/editor/time.ts';

/** One cue as the editor table holds it: raw ticks, so a time box can be compared exactly. */
export interface EditorRow {
  readonly id: string;
  /** 1-based position in the document as it is now. */
  readonly n: number;
  readonly start: number;
  readonly end: number;
  readonly text: string;
}

export type TimeField = 'start' | 'end';
export type Field = TimeField | 'text';

/** Why a field is flagged. A time the parser refused, or an edit the worker refused. */
export type DraftError = CueTimeError | EditRefusal;

/**
 * What the user typed into a cue and has not had accepted, keyed by cue id rather than by row
 * element: the table's rows are a recycled pool, and a flagged time must still be there, still
 * flagged, when the row scrolls back into view.
 */
export interface Draft {
  readonly start?: string;
  readonly end?: string;
  readonly error: DraftError | null;
  /** Which box the error belongs to, so only that one is marked invalid. */
  readonly errorField: TimeField | null;
}

export type TimeCommit =
  /** The box says what the cue already says. */
  | { readonly kind: 'same' }
  /** Flag it; send nothing. */
  | { readonly kind: 'invalid'; readonly code: DraftError; readonly field: TimeField }
  /** Send this. Only the sides that change are set. */
  | { readonly kind: 'edit'; readonly start?: number; readonly end?: number };

/**
 * A time box committed (Enter, or focus leaving it). `other` is what the OTHER box of the same cue
 * holds when it has an unaccepted draft: a start moved past the old end is flagged, and when the
 * end is then typed after it, the two go together as one edit.
 */
export function commitTime(
  row: EditorRow,
  field: TimeField,
  typed: string,
  other: string | undefined,
): TimeCommit {
  const parsed = parseCueTime(typed);
  if (!parsed.ok) return { kind: 'invalid', code: parsed.code, field };
  const otherField: TimeField = field === 'start' ? 'end' : 'start';
  let otherTicks: number = row[otherField];
  let otherChanged = false;
  if (other !== undefined) {
    const o = parseCueTime(other);
    if (o.ok) {
      otherTicks = o.ticks;
      otherChanged = otherTicks !== row[otherField];
    }
  }
  const start = field === 'start' ? (parsed.ticks as number) : otherTicks;
  const end = field === 'end' ? (parsed.ticks as number) : otherTicks;
  if (end <= start) return { kind: 'invalid', code: 'end-not-after-start', field };
  const changed = (parsed.ticks as number) !== row[field];
  if (!changed && !otherChanged) return { kind: 'same' };
  return {
    kind: 'edit',
    ...(start !== row.start ? { start } : {}),
    ...(end !== row.end ? { end } : {}),
  };
}

/**
 * Where a split cuts. The caret, when it sits inside the text; otherwise null, which the worker
 * reads as "in half by time" (at the line break or the word nearest the middle). A caret at
 * either end would make an empty cue, which is never what the button meant.
 */
export function splitAt(text: string, caret: number | null): number | null {
  if (caret === null || !Number.isInteger(caret)) return null;
  return caret > 0 && caret < text.length ? caret : null;
}

/** What a row shows, given its cue, any draft for it, and what is selected and playing. */
export interface RowView {
  readonly n: string;
  readonly start: string;
  readonly end: string;
  readonly text: string;
  readonly startInvalid: boolean;
  readonly endInvalid: boolean;
  /** The error to say under the times, or null. */
  readonly error: DraftError | null;
  readonly selected: boolean;
  readonly current: boolean;
}

export function rowView(
  row: EditorRow,
  draft: Draft | undefined,
  selectedId: string | null,
  currentId: string | null,
): RowView {
  return {
    n: String(row.n),
    start: draft?.start ?? formatCueTime(row.start),
    end: draft?.end ?? formatCueTime(row.end),
    text: row.text,
    startInvalid: draft?.errorField === 'start' && draft.error !== null,
    endInvalid: draft?.errorField === 'end' && draft.error !== null,
    error: draft?.error ?? null,
    selected: row.id === selectedId,
    current: row.id === currentId,
  };
}

export type EditorKey = 'undo' | 'redo' | null;

/**
 * Ctrl/Cmd+Z undoes, Ctrl/Cmd+Shift+Z and Ctrl+Y redo, EXCEPT inside a box with typing of its
 * own not yet committed: there the browser's own undo is the one the user means.
 */
export function editorKey(
  ev: {
    readonly key: string;
    readonly ctrlKey: boolean;
    readonly metaKey: boolean;
    readonly shiftKey: boolean;
    readonly altKey: boolean;
  },
  inDirtyField: boolean,
): EditorKey {
  if (inDirtyField || ev.altKey || !(ev.ctrlKey || ev.metaKey)) return null;
  const key = ev.key.toLowerCase();
  if (key === 'z') return ev.shiftKey ? 'redo' : 'undo';
  if (key === 'y' && !ev.shiftKey) return 'redo';
  return null;
}

/**
 * The format a file is saved in first. A page that names one (`pageFormat`: SRT on /srt-editor,
 * VTT on /vtt-editor, from the row's `to_format`) starts there whatever the file came in. Otherwise
 * the file's own format, when the engine writes it back, else the first.
 */
export function defaultSaveFormat(
  opened: string,
  writes: readonly WriteFormat[],
  pageFormat?: string | null,
): WriteFormat | null {
  const page = pageFormat ? writes.find((f) => f === pageFormat) : undefined;
  if (page) return page;
  const own = writes.find((f) => f === opened);
  return own ?? writes[0] ?? null;
}

/** Ticks ↔ a media element's seconds. */
export function ticksToSeconds(ticks: number, ticksPerSecond: number): number {
  return ticks / ticksPerSecond;
}

export function secondsToTicks(seconds: number, ticksPerSecond: number): number {
  return Math.max(0, Math.round(seconds * ticksPerSecond));
}

/*───────────────────────────────────────────────────────────────────────────────────────
  The row source
───────────────────────────────────────────────────────────────────────────────────────*/

/** Rows per `preview` request: the same page size as lib/cue-window.ts, for the same reasons. */
export const EDITOR_PAGE = 256;
const MAX_PAGES = 8;

function editorRowOf(row: CuePreviewRow, at: number): EditorRow {
  return { id: row.id, n: at + 1, start: row.startTicks, end: row.endTicks, text: row.text };
}

export interface EditorWindowOptions {
  readonly fetchPage: (from: number, count: number) => Promise<PreviewPayload>;
  readonly onPage: () => void;
}

export interface EditorWindow {
  count(): number;
  /** Cheap and synchronous; null for a row not loaded yet (and its page is then requested). */
  row(index: number): EditorRow | null;
  reset(docId: DocumentId | null, total: number): void;
  /**
   * The document changed. Pages already held keep answering until their fresh copies land, so an
   * edit repaints in place instead of blanking the table for a round trip. A stale row can only
   * ever name a cue by id, and an edit to a cue that is gone is refused, never misapplied.
   */
  invalidate(total: number): void;
}

export function createEditorWindow(options: EditorWindowOptions): EditorWindow {
  const pages = new Map<number, readonly EditorRow[]>();
  const stale = new Set<number>();
  const inFlight = new Map<number, number>();
  let docId: DocumentId | null = null;
  let total = 0;
  /** Bumped by every reset and invalidate: a page fetched before it is stale on arrival. */
  let generation = 0;

  function evict(): void {
    while (pages.size > MAX_PAGES) {
      const oldest = pages.keys().next();
      if (oldest.done) return;
      pages.delete(oldest.value);
      stale.delete(oldest.value);
    }
  }

  function request(page: number): void {
    if (docId === null) return;
    if (inFlight.get(page) === generation) return;
    if (pages.has(page) && !stale.has(page)) return;
    const asked = generation;
    const askedDoc = docId;
    inFlight.set(page, asked);
    options
      .fetchPage(page * EDITOR_PAGE, EDITOR_PAGE)
      .then((payload) => {
        if (askedDoc !== docId || payload.docId !== docId) return;
        if (asked !== generation) {
          // Superseded by a later edit. Still better than nothing for a page with no copy yet,
          // and marked stale so the fresh one follows.
          if (pages.has(page)) return;
          stale.add(page);
        } else {
          stale.delete(page);
          total = payload.total;
        }
        pages.delete(page);
        pages.set(
          page,
          payload.rows.map((row, i) => editorRowOf(row, payload.from + i)),
        );
        evict();
        options.onPage();
      })
      .catch(() => {
        // The row stays as it was; the next paint asks again.
      })
      .finally(() => {
        if (inFlight.get(page) === asked) inFlight.delete(page);
      });
  }

  return {
    count: () => total,
    row(index) {
      if (index < 0 || index >= total) return null;
      const page = Math.floor(index / EDITOR_PAGE);
      const held = pages.get(page);
      if (held === undefined || stale.has(page)) request(page);
      const offset = index - page * EDITOR_PAGE;
      if (offset > EDITOR_PAGE - 32) request(page + 1);
      else if (offset < 32 && page > 0) request(page - 1);
      return held?.[offset] ?? null;
    },
    reset(nextDocId, nextTotal) {
      docId = nextDocId;
      total = nextTotal;
      generation += 1;
      pages.clear();
      stale.clear();
      inFlight.clear();
    },
    invalidate(nextTotal) {
      total = nextTotal;
      generation += 1;
      for (const page of pages.keys()) stale.add(page);
    },
  };
}
